import React, { useState } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowLeft, ArrowRight, CheckCircle, XCircle, Phone, Mail, User, MessageSquare, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { mockEligibilityCheck } from "../utils/mockData";
import Seo from "../components/Seo";
import { serviceJsonLd, filArianeJsonLd } from "../utils/donneesStructurees";
import { plafond, formaterEuros } from "../utils/plafonds";
import ChampAntiRobot from "../components/ChampAntiRobot";
import { trackFormSubmit } from "../utils/analytics";
import { attributionCourante } from "../utils/attribution";

// Composant formulaire de contact intégré
const ContactForm = () => {
  const [contactData, setContactData] = useState({
    nom: "", telephone: "", email: "", besoin: "", creneau: "", website: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      // Préparer les données pour l'API
      const apiData = {
        nom: contactData.nom,
        telephone: contactData.telephone,
        email: contactData.email,
        besoin: contactData.besoin,
        creneau: contactData.creneau,
        source: 'simulateur_eligibilite',
        website: contactData.website
      };

      // Appel API
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/send-contact-form`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...apiData, attribution: attributionCourante() }),
      });

      const result = await response.json();

      if (result.success) {
        trackFormSubmit('simulateur');
        setSubmitStatus({
          type: 'success',
          message: '✅ Demande envoyée ! Un conseiller vous contactera sous 24h ouvrées.'
        });
        // Reset du formulaire
        setContactData({ nom: "", telephone: "", email: "", besoin: "", creneau: "", website: "" });
      } else {
        throw new Error(result.message || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Erreur envoi formulaire contact:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Erreur lors de l\'envoi. Veuillez réessayer ou nous appeler directement au 09 39 03 54 44.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-2xl border-2 border-orange-200 shadow-lg">
      <div className="text-center mb-6">
        <h4 className="text-xl font-bold text-gray-900 mb-2">
          📞 Être accompagné par notre AMO
        </h4>
        <p className="text-gray-700">
          Diagnostic gratuit et montage de votre dossier MaPrimeAdapt' en 10 jours
        </p>
      </div>

      {submitStatus ? (
        <div className="text-center py-8">
          <div className="bg-green-50 border border-green-200 text-green-800 p-4 rounded-lg">
            {submitStatus.message}
          </div>
        </div>
      ) : (
        <form onSubmit={handleContactSubmit} className="space-y-4">
          <ChampAntiRobot
            id="website-simulateur"
            valeur={contactData.website}
            onChange={(e) => setContactData(prev => ({...prev, website: e.target.value}))}
          />
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-gray-700">Nom complet *</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  value={contactData.nom}
                  onChange={(e) => setContactData(prev => ({...prev, nom: e.target.value}))}
                  className="pl-10"
                  placeholder="Votre nom"
                  required
                />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">Téléphone *</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="tel"
                  value={contactData.telephone}
                  onChange={(e) => setContactData(prev => ({...prev, telephone: e.target.value}))}
                  className="pl-10"
                  placeholder="06 12 34 56 78"
                  required
                />
              </div>
            </div>
          </div>

          <div>
            <Label className="text-sm font-medium text-gray-700">Email *</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="email"
                value={contactData.email}
                onChange={(e) => setContactData(prev => ({...prev, email: e.target.value}))}
                className="pl-10"
                placeholder="votre@email.fr"
                required
              />
            </div>
          </div>

          <div>
            <Label className="text-sm font-medium text-gray-700">Votre projet d'adaptation</Label>
            <div className="relative">
              <MessageSquare className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <textarea
                value={contactData.besoin}
                onChange={(e) => setContactData(prev => ({...prev, besoin: e.target.value}))}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
                rows="3"
                placeholder="Salle de bain, monte-escalier, volets roulants..."
              />
            </div>
          </div>

          <div>
            <Label className="text-sm font-medium text-gray-700">Créneau de contact préféré</Label>
            <Select value={contactData.creneau} onValueChange={(value) => setContactData(prev => ({...prev, creneau: value}))}>
              <SelectTrigger>
                <SelectValue placeholder="Quand préférez-vous être contacté ?" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="matin">Matin (9h-12h)</SelectItem>
                <SelectItem value="apres-midi">Après-midi (14h-17h)</SelectItem>
                <SelectItem value="soir">Début de soirée (17h-19h)</SelectItem>
                <SelectItem value="indifferent">Indifférent</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button 
            type="submit" 
            className="w-full bg-gradient-to-r from-orange-600 to-pink-600 hover:from-orange-700 hover:to-pink-700 text-white font-medium py-3"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                Envoi en cours...
              </>
            ) : (
              <>
                <Phone className="mr-2 h-4 w-4" />
                Demander un rappel gratuit
              </>
            )}
          </Button>

          <p className="text-xs text-gray-700 text-center">
            Appel gratuit - Engagement de rappel sous 24h ouvrées
          </p>
        </form>
      )}
    </div>
  );
};

const Simulator = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    age: "",
    nbPersonnes: "1", 
    codePostal: "",
    revenus: "",
    statutOccupation: "",
    situationSpeciale: ""
  });
  const [result, setResult] = useState(null);

  const calculateEligibility = (data) => {
    // Logique d'éligibilité stricte MaPrimeAdapt'
    console.log("Calculating eligibility for:", data); // Debug
    
    // 1. Vérification de l'âge et situation
    let ageEligible = false;
    if (data.age === "70-plus") {
      ageEligible = true; // 70+ toujours éligible
    } else if (data.age === "60-69") {
      ageEligible = data.situationSpeciale !== "autonome"; // 60-69 seulement si pas autonome
    } else if (data.age === "moins-60") {
      ageEligible = data.situationSpeciale !== "aucune" && data.situationSpeciale !== "autonome"; // Moins de 60 seulement si handicap
    }
    
    // 2. Vérification propriétaire (corrigé : statutOccupation au lieu de statusLogement)
    const proprietaireEligible = data.statutOccupation === "proprietaire" || data.statutOccupation === "locataire-prive";
    
    // 3. Vérification département (Île-de-France)
    let departementEligible = false;
    if (data.codePostal && data.codePostal.length >= 2) {
      const dept = data.codePostal.substring(0, 2);
      const validDepts = ["75", "77", "91", "92", "93", "95"]; // Départements habilités
      departementEligible = validDepts.includes(dept);
    }
    
    // 4. Calcul du taux d'aide selon revenus
    let tauxAide = 0;
    if (data.revenus === "tres-modestes") {
      tauxAide = 70;
    } else if (data.revenus === "modestes") {
      tauxAide = 50;
    } else if (data.revenus === "superieurs") {
      tauxAide = 20;
    }
    
    // Éligibilité finale
    const eligible = ageEligible && proprietaireEligible && departementEligible;
    
    console.log("Eligibility result:", { 
      ageEligible, 
      proprietaireEligible, 
      departementEligible, 
      eligible, 
      tauxAide 
    }); // Debug
    
    return {
      eligible,
      tauxAide,
      montantEstime: eligible ? (tauxAide === 70 ? 15400 : tauxAide === 50 ? 11000 : 4400) : 0,
      montantMaximum: eligible ? (tauxAide === 70 ? 22000 : tauxAide === 50 ? 22000 : 22000) : 0, // Montant maximum MaPrimeAdapt'
      aide: eligible ? `${tauxAide}%` : "0%",
      message: eligible ? "Félicitations ! Vous êtes éligible à MaPrimeAdapt'." : "Vous n'êtes pas éligible selon les critères actuels.",
      raisons: !eligible ? {
        age: !ageEligible,
        proprietaire: !proprietaireEligible,
        departement: !departementEligible
      } : null
    };
  };

  const handleNext = () => {
    if (step < 4) {
      setStep(step + 1);
    } else {
      // Calculer l'éligibilité et passer au résultat
      const eligibilityResult = calculateEligibility(formData);
      setResult(eligibilityResult);
      setStep(5);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const isStepValid = () => {
    switch (step) {
      case 1:
        return formData.age && formData.situationSpeciale;
      case 2:
        return formData.nbPersonnes;
      case 3:
        return formData.codePostal && formData.codePostal.length === 5;
      case 4:
        return formData.revenus && formData.statutOccupation;
      default:
        return false;
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Messages immédiats pour certains cas
    if (field === "age" && value === "moins-60") {
      // Pas de message immédiat, on laisse continuer le parcours
    }
    if (field === "situationSpeciale" && value === "aucune") {
      // Message d'information sur l'autonomie
    }
  };

  // Fonction pour vérifier les messages d'alerte immédiats
  const getImmediateAlert = () => {
    // Cas : moins de 60 ans + aucune situation spéciale
    if (formData.age === "moins-60" && formData.situationSpeciale === "aucune") {
      return {
        type: "warning",
        message: "⚠️ MaPrimeAdapt' est principalement destinée aux personnes de 60 ans et plus ou en situation de handicap. Vérifiez votre éligibilité."
      };
    }

    // Cas : Code postal hors Île-de-France
    if (formData.codePostal && formData.codePostal.length === 5) {
      const dept = formData.codePostal.substring(0, 2);
      const validDepts = ["75", "77", "78", "91", "92", "93", "94", "95"];
      if (!validDepts.includes(dept)) {
        return {
          type: "error",
          message: "❌ Notre service n'est pas encore disponible dans votre département. Actuellement limité à l'Île-de-France."
        };
      }
    }

    return null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-white">
      <Seo
        titre="Simulateur MaPrimeAdapt' : simulation d'éligibilité en 4 min"
        description="Votre simulation MaPrimeAdapt' en 4 minutes : éligibilité, montant de l'aide et reste à charge estimés. Gratuit, sans engagement. Lancez le simulateur."
        chemin="/simulateur"
        donnees={[
          serviceJsonLd({
            nom: "Simulateur d'éligibilité MaPrimeAdapt'",
            description: "Simulation gratuite et immédiate de l'éligibilité à MaPrimeAdapt' et du taux de prise en charge des travaux.",
            url: "/simulateur",
          }),
          filArianeJsonLd([
            { nom: "Accueil", url: "/" },
            { nom: "Simulateur d'éligibilité", url: "/simulateur" },
          ]),
        ]}
      />
      <main id="contenu" className="container mx-auto px-4 py-8">
        <Link 
          to="/" 
          className="inline-flex items-center text-orange-700 hover:text-orange-700 mb-8 transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour à l'accueil
        </Link>

        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            {/* Section supprimée - doublon */}
            
            {/* Logos partenaires */}
            <div className="flex justify-center items-center space-x-8 mb-8">
              <div className="bg-white rounded-xl p-4 shadow-md border border-gray-100">
                <img 
                  src="/images/logo-tef.webp"
                  width="184"
                  height="80"
                  loading="lazy"
                  alt="Transition Énergie France"
                  className="h-12 w-auto object-contain"
                />
              </div>
              <div className="text-gray-400 text-2xl">×</div>
              <div className="bg-white rounded-xl p-4 shadow-md border border-gray-100">
                <img 
                  src="/images/logo-anah.webp"
                  width="500"
                  height="430"
                  loading="lazy"
                  alt="ANAH - Agence nationale de l'habitat"
                  className="h-12 w-auto object-contain"
                />
              </div>
              <div className="text-gray-400 text-2xl">×</div>
              <div className="bg-white rounded-xl p-4 shadow-md border border-gray-100">
                <img 
                  src="/images/logo-france-renov.webp"
                  width="500"
                  height="285"
                  loading="lazy"
                  alt="France Rénov' - Service public"
                  className="h-12 w-auto object-contain"
                />
              </div>
            </div>

            <div className="inline-flex items-center px-6 py-3 bg-white rounded-full shadow-lg mb-6 border border-orange-200">
              <div className="text-orange-700 font-bold text-lg mr-3">MaPrimeAdapt'</div>
              <div className="text-sm text-gray-700">L'aide de l'État pour adapter votre logement</div>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Simulateur d'éligibilité MaPrimeAdapt'
            </h1>
            <p className="text-gray-700">
              Faites votre simulation MaPrimeAdapt' en 4 minutes : éligibilité, montant de l'aide
              et reste à charge estimés. Gratuit et sans engagement.
            </p>
            <div className="flex justify-center mt-6">
              <div className="flex space-x-2">
                {[1, 2, 3, 4].map((stepNumber) => (
                  <div
                    key={stepNumber}
                    className={`h-2 w-12 rounded-full transition-colors ${
                      step >= stepNumber ? "bg-orange-500" : "bg-gray-200"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Messages d'alerte immédiats */}
          {(() => {
            const alert = getImmediateAlert();
            if (!alert) return null;
            
            return (
              <div className={`max-w-2xl mx-auto mb-6 p-4 rounded-xl border-2 ${
                alert.type === 'error' 
                  ? 'bg-red-50 border-red-200 text-red-800' 
                  : 'bg-yellow-50 border-yellow-200 text-yellow-800'
              }`}>
                <p className="text-center font-medium">{alert.message}</p>
              </div>
            );
          })()}

          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle>
                {step === 1 && "Votre situation"}
                {step === 2 && "Votre foyer"}
                {step === 3 && "Votre logement"}
                {step === 4 && "Situation financière"}
                {step === 5 && "Résultat de votre simulation"}
              </CardTitle>
              <CardDescription>
                {step === 1 && "Étape 1/4 - Quelques informations sur vous"}
                {step === 2 && "Étape 2/4 - Composition de votre foyer"}
                {step === 3 && "Étape 3/4 - Localisation de votre logement"}
                {step === 4 && "Étape 4/4 - Vos ressources et statut"}
                {step === 5 && "Votre éligibilité à MaPrimeAdapt'"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {step === 1 && (
                <div className="space-y-8">
                  {/* Visuel senior avec icône */}
                  <div className="text-center mb-8">
                    <div className="w-24 h-24 mx-auto bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mb-4">
                      <div className="text-4xl">👤</div>
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900">Parlez-nous de vous</h2>
                  </div>

                  <div>
                    <Label className="text-lg font-medium text-gray-800 mb-6 block text-center">
                      Quel est votre âge ?
                    </Label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div 
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${formData.age === "moins-60" ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-300'}`}
                        onClick={() => updateFormData("age", "moins-60")}
                      >
                        <div className="text-center">
                          <div className="text-3xl mb-2">🧑‍💼</div>
                          <div className="font-medium text-gray-900">Moins de 60 ans</div>
                        </div>
                      </div>
                      <div 
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${formData.age === "60-69" ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-300'}`}
                        onClick={() => updateFormData("age", "60-69")}
                      >
                        <div className="text-center">
                          <div className="text-3xl mb-2">👨‍💼</div>
                          <div className="font-medium text-gray-900">60 - 69 ans</div>
                        </div>
                      </div>
                      <div 
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${formData.age === "70-plus" ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-300'}`}
                        onClick={() => updateFormData("age", "70-plus")}
                      >
                        <div className="text-center">
                          <div className="text-3xl mb-2">👵</div>
                          <div className="font-medium text-gray-900">70 ans et plus</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {formData.age && (
                    <div>
                      <Label className="text-lg font-medium text-gray-800 mb-6 block text-center">
                        Quelle est votre situation ?
                      </Label>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div 
                          className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${formData.situationSpeciale === "autonome" ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-green-300'}`}
                          onClick={() => updateFormData("situationSpeciale", "autonome")}
                        >
                          <div className="text-center">
                            <div className="text-3xl mb-2">🚶‍♂️</div>
                            <div className="font-medium text-gray-900">Autonome</div>
                            <div className="text-xs text-gray-700">Je me déplace sans aide</div>
                          </div>
                        </div>
                        <div 
                          className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${formData.situationSpeciale === "perte-autonomie" ? 'border-orange-500 bg-orange-50' : 'border-gray-300 hover:border-orange-300'}`}
                          onClick={() => updateFormData("situationSpeciale", "perte-autonomie")}
                        >
                          <div className="text-center">
                            <div className="text-3xl mb-2">🦯</div>
                            <div className="font-medium text-gray-900">Perte d'autonomie</div>
                            <div className="text-xs text-gray-700">GIR 1 à 6</div>
                          </div>
                        </div>
                        <div 
                          className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${formData.situationSpeciale === "handicap" ? 'border-purple-500 bg-purple-50' : 'border-gray-300 hover:border-purple-300'}`}
                          onClick={() => updateFormData("situationSpeciale", "handicap")}
                        >
                          <div className="text-center">
                            <div className="text-3xl mb-2">♿</div>
                            <div className="font-medium text-gray-900">Situation de handicap</div>
                            <div className="text-xs text-gray-700">Reconnu officiellement</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Message d'inéligibilité avec visuel */}
                  {formData.age && formData.situationSpeciale && 
                   ((formData.age === "moins-60" || formData.age === "60-69") && formData.situationSpeciale === "autonome") && (
                    <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 text-center">
                      <div className="text-4xl mb-3">❌</div>
                      <h4 className="text-lg font-bold text-red-800 mb-2">
                        Non éligible à MaPrimeAdapt'
                      </h4>
                      <p className="text-red-700">
                        {formData.age === "moins-60" 
                          ? "MaPrimeAdapt' est réservé aux personnes de 70 ans et plus, ou en situation de handicap, ou de 60-69 ans avec perte d'autonomie."
                          : "Entre 60 et 69 ans, vous devez justifier d'une perte d'autonomie évaluée (GIR 1 à 6)."
                        }
                      </p>
                    </div>
                  )}
                </div>
              )}

              {step === 2 && (
                <div className="space-y-8">
                  <div className="text-center mb-8">
                    <div className="w-24 h-24 mx-auto bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center mb-4">
                      <div className="text-4xl">👥</div>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">Votre foyer</h3>
                  </div>

                  <div>
                    <Label className="text-lg font-medium text-gray-800 mb-6 block text-center">
                      Combien de personnes vivent dans votre logement ?
                    </Label>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      {[1, 2, 3, 4, 5].map((nb) => (
                        <div 
                          key={nb}
                          className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${formData.nbPersonnes === nb.toString() ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-green-300'}`}
                          onClick={() => updateFormData("nbPersonnes", nb.toString())}
                        >
                          <div className="text-center">
                            <div className="text-2xl mb-2">
                              {nb === 1 ? '👤' : nb === 2 ? '👫' : nb === 3 ? '👨‍👩‍👧' : nb === 4 ? '👨‍👩‍👧‍👦' : '👨‍👩‍👧‍👦+'}
                            </div>
                            <div className="font-medium text-gray-900">
                              {nb} {nb === 5 ? '+' : ''} pers{nb > 1 ? 'onnes' : 'onne'}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-8">
                  <div className="text-center mb-8">
                    <div className="w-24 h-24 mx-auto bg-gradient-to-br from-purple-100 to-purple-200 rounded-full flex items-center justify-center mb-4">
                      <div className="text-4xl">🏠</div>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">Votre logement</h3>
                  </div>

                  <div>
                    <Label className="text-lg font-medium text-gray-800 mb-6 block text-center">
                      Quel est le code postal de votre logement ?
                    </Label>
                    <div className="max-w-md mx-auto">
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-2xl">
                          📍
                        </div>
                        <input
                          type="text"
                          value={formData.codePostal}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 5);
                            updateFormData("codePostal", value);
                          }}
                          placeholder="Ex: 75001, 77100..."
                          className="w-full pl-12 pr-4 py-4 text-lg border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-center font-medium"
                          maxLength="5"
                        />
                      </div>
                      {formData.codePostal && formData.codePostal.length === 5 && (
                        <div className="mt-4 text-center">
                          <div className="inline-flex items-center px-3 py-2 bg-purple-50 text-purple-700 rounded-lg text-sm">
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Département {formData.codePostal.substring(0, 2)} détecté
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {step === 4 && (
                <div className="space-y-8">
                  <div className="text-center mb-8">
                    <div className="w-24 h-24 mx-auto bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-full flex items-center justify-center mb-4">
                      <div className="text-4xl">💰</div>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">Situation financière</h3>
                  </div>

                  <div>
                    <Label className="text-lg font-medium text-gray-800 mb-6 block text-center">
                      Quel est votre statut d'occupation ?
                    </Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                      <div 
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${formData.statutOccupation === "proprietaire" ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-300'}`}
                        onClick={() => updateFormData("statutOccupation", "proprietaire")}
                      >
                        <div className="text-center">
                          <div className="text-3xl mb-2">🏠</div>
                          <div className="font-medium text-gray-900">Propriétaire</div>
                          <div className="text-xs text-gray-700">occupant</div>
                        </div>
                      </div>
                      <div 
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${formData.statutOccupation === "locataire-prive" ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-300'}`}
                        onClick={() => updateFormData("statutOccupation", "locataire-prive")}
                      >
                        <div className="text-center">
                          <div className="text-3xl mb-2">🏢</div>
                          <div className="font-medium text-gray-900">Locataire</div>
                          <div className="text-xs text-gray-700">privé</div>
                        </div>
                      </div>
                      <div 
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${formData.statutOccupation === "locataire-social" ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-300'}`}
                        onClick={() => updateFormData("statutOccupation", "locataire-social")}
                      >
                        <div className="text-center">
                          <div className="text-3xl mb-2">🏘️</div>
                          <div className="font-medium text-gray-900">Locataire</div>
                          <div className="text-xs text-gray-700">social</div>
                        </div>
                      </div>
                      <div 
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${formData.statutOccupation === "heberge" ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-300'}`}
                        onClick={() => updateFormData("statutOccupation", "heberge")}
                      >
                        <div className="text-center">
                          <div className="text-3xl mb-2">🤝</div>
                          <div className="font-medium text-gray-900">Hébergé</div>
                          <div className="text-xs text-gray-700">gratuit</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="text-lg font-medium text-gray-800 mb-6 block text-center">
                      Quels sont vos revenus annuels ?
                    </Label>
                    <div className="text-sm text-gray-700 text-center mb-4">
                      Revenu fiscal de référence (RFR) de votre foyer
                    </div>
					<div className="text-center mb-6">
                   <button 
                   onClick={() => window.open(
                   'https://france-renov.gouv.fr/bareme', 
                   'baremesIDF', 
                   'width=1100,height=700,scrollbars=yes,resizable=yes,toolbar=no,location=yes'
                   )}
                   className="text-blue-600 hover:underline text-sm cursor-pointer bg-transparent border-none"
                   >
                   📊 Barèmes MaPrimeAdapt' Île-de-France
                    </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div 
                        className={`p-6 rounded-xl border-2 cursor-pointer transition-all ${formData.revenus === "tres-modestes" ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-300'}`}
                        onClick={() => updateFormData("revenus", "tres-modestes")}
                      >
                        <div className="text-center">
                          <div className="text-4xl mb-3">💙</div>
                          <div className="font-bold text-blue-700 mb-2">Revenus très modestes</div>
                          <div className="text-sm text-gray-700">Jusqu'à 70% d'aide</div>
                          <div className="text-xs text-blue-700 mt-2">
                            ≤ {formaterEuros(plafond("tresModestes", Number(formData.nbPersonnes)))} de revenu fiscal de référence
                          </div>
                        </div>
                      </div>
                      <div 
                        className={`p-6 rounded-xl border-2 cursor-pointer transition-all ${formData.revenus === "modestes" ? 'border-yellow-500 bg-yellow-50' : 'border-gray-300 hover:border-yellow-300'}`}
                        onClick={() => updateFormData("revenus", "modestes")}
                      >
                        <div className="text-center">
                          <div className="text-4xl mb-3">💛</div>
                          <div className="font-bold text-yellow-700 mb-2">Revenus modestes</div>
                          <div className="text-sm text-gray-700">Jusqu'à 50% d'aide</div>
                          <div className="text-xs text-yellow-700 mt-2">
                            ≤ {formaterEuros(plafond("modestes", Number(formData.nbPersonnes)))} de revenu fiscal de référence
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="text-center mt-4">
                      <div 
                        className={`inline-block p-4 rounded-xl border-2 cursor-pointer transition-all ${formData.revenus === "superieures" ? 'border-purple-500 bg-purple-50' : 'border-gray-300 hover:border-purple-300'}`}
                        onClick={() => updateFormData("revenus", "superieures")}
                      >
                        <div className="text-center">
                          <div className="text-3xl mb-2">💜</div>
                          <div className="font-medium text-purple-700">Revenus supérieurs</div>
                          <div className="text-xs text-purple-600">Non éligible MaPrimeAdapt'</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {step === 5 && result && (
                <div className="space-y-8">
                  {result.eligible ? (
                    // Résultat ÉLIGIBLE avec design MaPrimeAdapt'
                    <div className="space-y-8">
                      {/* Header avec visuel MaPrimeAdapt' */}
                      <div className="bg-gradient-to-r from-orange-500 to-pink-500 text-white p-8 rounded-2xl text-center">
                        <CheckCircle className="h-16 w-16 mx-auto mb-4 opacity-90" />
                        <h3 className="text-2xl font-bold mb-2">🎉 Félicitations !</h3>
                        <p className="text-lg mb-4">Vous êtes éligible à MaPrimeAdapt'</p>
                        <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 mt-6">
                          <div className="text-orange-100 text-sm mb-2">Ma vie change, mon logement s'adapte</div>
                          <div className="text-2xl font-bold">MaPrimeAdapt'</div>
                        </div>
                      </div>

                      {/* Estimation de l'aide */}
                      <div className="bg-gradient-to-br from-orange-50 to-pink-50 p-6 rounded-2xl border-2 border-orange-200">
                        <h4 className="text-xl font-bold text-gray-900 mb-6 text-center">
                          💰 Estimation de votre aide
                        </h4>
                        <div className="grid md:grid-cols-2 gap-6">
                          <div className="text-center">
                            <div className="text-3xl font-bold text-orange-700">{result.tauxAide}%</div>
                            <div className="text-gray-700">Taux de prise en charge</div>
                          </div>
                          <div className="text-center">
                            <div className="text-3xl font-bold text-orange-700">{result.montantEstime.toLocaleString()} €</div>
                            <div className="text-gray-700">Aide maximum estimée</div>
                          </div>
                        </div>
                        <div className="text-xs text-gray-700 text-center mt-4 italic">
                          *Estimation indicative pour des travaux plafonnés à 22 000€ - Un diagnostic précis sera réalisé par notre AMO
                        </div>
                      </div>

                      {/* Formulaire de contact intégré */}
                      <ContactForm />

                      {/* Bouton refaire le test */}
                      <div className="text-center">
                        <Button 
                          variant="outline" 
                          onClick={() => {
                            setStep(1);
                            setFormData({
                              age: "",
                              nbPersonnes: "1", 
                              codePostal: "",
                              revenus: "",
                              statutOccupation: "",
                              situationSpeciale: ""
                            });
                            setResult(null);
                          }}
                          className="text-gray-700 hover:bg-gray-50"
                        >
                          🔄 Refaire le test
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // Résultat NON ÉLIGIBLE - Strict, aucune autre option
                    <div className="text-center space-y-8">
                      <div className="bg-gradient-to-br from-gray-100 to-gray-200 p-8 rounded-2xl">
                        <XCircle className="h-16 w-16 mx-auto mb-4 text-gray-700" />
                        <h3 className="text-2xl font-bold text-gray-800 mb-4">
                          Non éligible à MaPrimeAdapt'
                        </h3>
                        <div className="bg-white p-6 rounded-xl mx-auto max-w-md">
                          <p className="text-gray-700 leading-relaxed">
                            {result.message}
                          </p>
                        </div>
                      </div>

                      {/* Informations MaPrimeAdapt' en grisé */}
                      <div className="bg-gray-50 p-6 rounded-xl border border-gray-300 opacity-60">
                        <div className="text-gray-400 text-sm mb-2">Ma vie change, mon logement s'adapte</div>
                        <div className="text-xl font-bold text-gray-400">MaPrimeAdapt'</div>
                        <div className="text-xs text-gray-400 mt-2">Dispositif de l'État - ANAH</div>
                      </div>

                      {/* Seulement le bouton refaire le test */}
                      <div>
                        <Button 
                          variant="outline" 
                          onClick={() => {
                            setStep(1);
                            setFormData({
                              age: "",
                              nbPersonnes: "1", 
                              codePostal: "",
                              revenus: "",
                              statutOccupation: "",
                              situationSpeciale: ""
                            });
                            setResult(null);
                          }}
                          className="bg-gray-100 hover:bg-gray-200 text-gray-700"
                        >
                          🔄 Refaire le test avec d'autres informations
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {step < 5 && (
                <div className="flex justify-between pt-6">
                  <Button
                    variant="outline"
                    onClick={handleBack}
                    disabled={step === 1}
                    className="flex items-center space-x-2"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span>Précédent</span>
                  </Button>
                  <Button
                    onClick={handleNext}
                    disabled={!isStepValid()}
                    className="bg-orange-700 hover:bg-orange-800 flex items-center space-x-2"
                  >
                    <span>{step === 4 ? "Voir le résultat" : "Suivant"}</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default Simulator;
